import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './Privacy.module.css'

// ── ICONS ──────────────────────────────────────────────────────────────────────
const ShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
)
const LockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
)
const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
)
const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)
const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
)
const BotIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="10" rx="2"/>
    <circle cx="12" cy="5" r="2"/>
    <path d="M12 7v4"/>
    <circle cx="8" cy="16" r="1" fill="currentColor" stroke="none"/>
    <circle cx="16" cy="16" r="1" fill="currentColor" stroke="none"/>
  </svg>
)
const ZapIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
)
const DownloadIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
)
const UsersIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
)
const StatusIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
)
const MessageIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
)
const ArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
)

// ── SCROLL REVEAL ─────────────────────────────────────────────────────────────
function useReveal() {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect() } },
      { threshold: 0.12 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])
  return [ref, visible]
}

// ── REVEAL SECTION ────────────────────────────────────────────────────────────
function Reveal({ children, delay = 0, className = '' }) {
  const [ref, visible] = useReveal()
  return (
    <div
      ref={ref}
      className={`${styles.reveal} ${visible ? styles.revealVisible : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  )
}

// ── TRUST BADGE ───────────────────────────────────────────────────────────────
function TrustBadge({ icon, label, yes }) {
  return (
    <div className={`${styles.trustBadge} ${yes ? styles.trustYes : styles.trustNo}`}>
      <span className={styles.trustIcon}>{yes ? <CheckIcon /> : <XIcon />}</span>
      <span className={styles.trustIcon2}>{icon}</span>
      <span className={styles.trustLabel}>{label}</span>
    </div>
  )
}

// ── FEATURE ROW ───────────────────────────────────────────────────────────────
function Feature({ icon, title, desc, delay }) {
  return (
    <Reveal delay={delay}>
      <div className={styles.featureRow}>
        <div className={styles.featureIcon}>{icon}</div>
        <div>
          <div className={styles.featureTitle}>{title}</div>
          <div className={styles.featureDesc}>{desc}</div>
        </div>
      </div>
    </Reveal>
  )
}

// ── SECTION ───────────────────────────────────────────────────────────────────
function Section({ title, body, items, delay = 0 }) {
  return (
    <Reveal delay={delay}>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>{title}</h3>
        {body && <p className={styles.sectionBody}>{body}</p>}
        {items && (
          <ul className={styles.list}>
            {items.map((item) => (
              <li key={item} className={styles.listItem}>
                <span className={styles.listCheck}><CheckIcon /></span>
                {item}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Reveal>
  )
}

// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function Privacy() {
  const [visible, setVisible] = useState(false)
  const navigate = useNavigate()
  useEffect(() => { setTimeout(() => setVisible(true), 60) }, [])

  return (
    <div className={`${styles.page} ${visible ? styles.visible : ''}`}>

      {/* ── HERO ── */}
      <div className={styles.hero}>
        <div className={styles.heroOrb1} />
        <div className={styles.heroOrb2} />
        <div className={styles.heroGrid} />

        <div className={styles.heroContent}>
          <div className={styles.breadcrumb}>
            <span>Saviya-X</span>
            <span className={styles.sep}>/</span>
            <span className={styles.current}>Privacy & Safety</span>
          </div>

          <div className={styles.heroBadge}>
            <ShieldIcon />
            <span>100% Safe · Zero Data Selling · Free Forever</span>
          </div>

          <h1 className={styles.heroTitle}>
            Your privacy.<br />
            <span className={styles.heroAccent}>Our commitment.</span>
          </h1>

          <p className={styles.heroSub}>
            Saviya-X is a free WhatsApp bot platform powered by WhatsApp Web API.
            We never store, sell, or share your personal data. No hacking. No surveillance.
            Just pure automation — fully transparent.
          </p>

          {/* TRUST BADGES */}
          <div className={styles.trustRow}>
            <TrustBadge icon={<EyeOffIcon />} label="No data selling" yes />
            <TrustBadge icon={<LockIcon />}   label="No hacking" yes />
            <TrustBadge icon={<ShieldIcon />} label="No surveillance" yes />
            <TrustBadge icon={<ZapIcon />}    label="100% Free" yes />
          </div>
        </div>
      </div>

      {/* ── WHAT IS SAVIYA-X ── */}
      <Reveal>
        <div className={styles.aboutCard}>
          <div className={styles.aboutLeft}>
            <div className={styles.aboutIconWrap}><BotIcon /></div>
            <h2 className={styles.aboutTitle}>What is Saviya-X?</h2>
            <p className={styles.aboutDesc}>
              Saviya-X is a <strong>WhatsApp Web API</strong> based bot platform.
              It runs entirely on the WhatsApp Web protocol — the same technology
              your browser uses when you open WhatsApp Web. There is no unofficial
              API exploitation, no account bypassing, and no data interception.
            </p>
            <p className={styles.aboutDesc}>
              This service is <strong>completely free</strong>. We take full responsibility
              for the platform's safety. Your sessions are isolated, temporary, and
              never exposed to other users.
            </p>
          </div>
          <div className={styles.aboutRight}>
            <div className={styles.featureGrid}>
              <Feature icon={<DownloadIcon />} title="Media Downloads" desc="Download images, videos, documents from any chat" delay={0} />
              <Feature icon={<UsersIcon />}    title="Group Management" desc="Manage groups, members, settings automatically" delay={80} />
              <Feature icon={<StatusIcon />}   title="Auto Status Seen" desc="Automatically view all contact statuses" delay={160} />
              <Feature icon={<MessageIcon />}  title="Auto Replies" desc="Set custom auto-reply rules for any message" delay={240} />
              <Feature icon={<ZapIcon />}      title="Instant Deploy" desc="Session live in under a second, no setup needed" delay={320} />
              <Feature icon={<ShieldIcon />}   title="Zero Storage" desc="No messages, media or contacts ever saved to disk" delay={400} />
            </div>
          </div>
        </div>
      </Reveal>

      {/* ── SAFETY COMPARISON ── */}
      <Reveal>
        <div className={styles.comparisonSection}>
          <div className={styles.compHeader}>
            <span className={styles.eyebrow}>Transparency</span>
            <h2 className={styles.compTitle}>What we do — and don't do</h2>
          </div>
          <div className={styles.compGrid}>
            <div className={styles.compCol}>
              <div className={styles.compColHeader} style={{ color: 'var(--green)' }}>
                <CheckIcon /> We do
              </div>
              {[
                'Use WhatsApp Web API (same as your browser)',
                'Run sessions in isolated temporary memory',
                'Encrypt all traffic over HTTPS/TLS',
                'Delete session data immediately on logout',
                'Provide this service completely free of charge',
                'Take full responsibility for platform safety',
                'Respect your right to stop at any time',
              ].map((t) => (
                <div key={t} className={`${styles.compItem} ${styles.compItemYes}`}>
                  <span className={styles.compCheck}><CheckIcon /></span>
                  {t}
                </div>
              ))}
            </div>
            <div className={styles.compCol}>
              <div className={styles.compColHeader} style={{ color: 'var(--red)' }}>
                <XIcon /> We never do
              </div>
              {[
                'Store your messages or media on our servers',
                'Sell or share your phone number with anyone',
                'Access your chats without your active session',
                'Use your account for spam or bulk messaging',
                'Retain any data after your session ends',
                'Inject malware, spyware, or tracking code',
                'Monetize your personal information in any way',
              ].map((t) => (
                <div key={t} className={`${styles.compItem} ${styles.compItemNo}`}>
                  <span className={styles.compCheck}><XIcon /></span>
                  {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Reveal>

      {/* ── MAIN LAYOUT ── */}
      <div className={styles.layout}>
        <div className={styles.mainCol}>

          <Section
            title="Data We Collect"
            body="Saviya-X collects only the phone number you submit for session deployment and basic request metadata (timestamp, server routing logs). We do not collect names, addresses, chat content, media, contacts, or any personally identifying information beyond the number required to create a WhatsApp Web session."
            delay={0}
          />

          <Section
            title="How Sessions Work"
            body="When you deploy a session, a WhatsApp Web connection is established in server memory — identical to opening web.whatsapp.com in your browser. The session exists only in RAM. Nothing is written to disk. When the session ends, all associated data is wiped immediately from memory. Your account credentials are never stored, logged, or transmitted beyond what WhatsApp Web requires."
            delay={50}
          />

          <Section
            title="Data Retention"
            body="Session data lives in active memory only, for the duration of your active session. Server request logs (IP, timestamp, route) are rotated on a 7-day cycle. No message content, media, contacts, or account credentials are ever persisted to any database or file system."
            delay={100}
          />

          <Section
            title="Third Party Infrastructure"
            body="Our servers are hosted on Railway (railway.app). Their infrastructure privacy policy applies to the underlying compute layer. We do not integrate any analytics platforms, advertising SDKs, or third-party tracking services. No data is ever shared with any third party."
            delay={150}
          />

          <Section
            title="Security"
            body="All API traffic is transmitted over HTTPS/TLS. Servers are network-isolated with no cross-session data access. We employ per-request server selection to prevent session correlation. The WhatsApp Web API is used strictly within its intended scope — no exploit, bypass, or unofficial modification is employed."
            delay={200}
          />

          <Section
            title="Your Rights"
            items={[
              'Request immediate termination of your active session',
              'Request confirmation that no data is held for your number',
              'Opt out of any future session deployments at any time',
              'Contact us to raise any privacy concern or question',
              'Use this service without creating any account or profile',
            ]}
            delay={250}
          />

          <Section
            title="Disclaimer & Responsibility"
            body="Saviya-X is a free, open-use automation platform. We take full responsibility for maintaining the safety and privacy of the platform. However, you are responsible for how you use bot features in compliance with WhatsApp's Terms of Service. Misuse (spam, harassment, bulk messaging) is strictly prohibited and may result in your WhatsApp account being restricted by WhatsApp Inc. — not by us."
            delay={300}
          />

          <Section
            title="Changes to This Policy"
            body='We may update this policy to reflect changes in our services or applicable law. The "Last updated" date at the top of this page always reflects the most recent revision. Continued use of Saviya-X after changes constitutes acceptance of the revised policy.'
            delay={350}
          />

        </div>

        {/* ── SIDEBAR ── */}
        <div className={styles.sidebar}>
          <div className={styles.sideSticky}>

            <Reveal>
              <div className={styles.sideCard}>
                <div className={styles.sideCardAccent} />
                <h4 className={styles.sideTitle}>Quick summary</h4>
                <div className={styles.summaryList}>
                  {[
                    { label: 'Data sold',     val: 'Never',     yes: true },
                    { label: 'Hacking',       val: 'Never',     yes: true },
                    { label: 'Spam',          val: 'Blocked',   yes: true },
                    { label: 'Cost',          val: 'Free',      yes: true },
                    { label: 'Session data',  val: 'RAM only',  yes: true },
                    { label: 'Retention',     val: '0 days',    yes: true },
                  ].map((s) => (
                    <div key={s.label} className={styles.summaryRow}>
                      <span className={styles.summaryLabel}>{s.label}</span>
                      <span className={`${styles.summaryVal} ${s.yes ? styles.summaryGreen : styles.summaryRed}`}>
                        {s.val}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <div className={styles.sideCard}>
                <h4 className={styles.sideTitle}>Contents</h4>
                <ul className={styles.tocList}>
                  {[
                    'Data We Collect',
                    'How Sessions Work',
                    'Data Retention',
                    'Third Party Infrastructure',
                    'Security',
                    'Your Rights',
                    'Disclaimer',
                    'Changes',
                  ].map((t, i) => (
                    <li key={t} className={styles.tocItem}>
                      <span className={styles.tocNum}>{String(i + 1).padStart(2, '0')}</span>
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <div className={`${styles.sideCard} ${styles.sideCtaCard}`}>
                <div className={styles.sideCtaOrb} />
                <ShieldIcon />
                <h4 className={styles.sideCtaTitle}>Privacy questions?</h4>
                <p className={styles.sideCtaDesc}>
                  We're transparent about everything. Ask us anything.
                </p>
                <button className={styles.sideCtaBtn} onClick={() => navigate('/contact')}>
                  Contact us <ArrowRight />
                </button>
              </div>
            </Reveal>

          </div>
        </div>
      </div>

      {/* ── FOOTER BANNER ── */}
      <Reveal>
        <div className={styles.footerBanner}>
          <div className={styles.footerBannerOrb} />
          <div className={styles.footerBannerIcon}><ShieldIcon /></div>
          <div>
            <div className={styles.footerBannerTitle}>Saviya-X is safe, free, and fully transparent.</div>
            <div className={styles.footerBannerSub}>
              Last updated March 2026 · Built on WhatsApp Web API · No data ever sold
            </div>
          </div>
        </div>
      </Reveal>

    </div>
  )
}
